import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUnit } from "effector-react";
import { useForm } from "react-hook-form";
import { $ideasStore, fetchIdeaDetailRequested } from "../store";
import { $expertiseTerms, $lookingForTerms } from "@/core/store/terms";
import { ideasService } from "../service/api";
import Loader from "@/core/components/loader/Loader";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { useToast } from "@/core/hooks/useToast";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { CommonModal } from "@/core/components/commonModal/CommonModal";
import "./EditIdeaPage.scss";
import { AxiosRequestConfig } from "axios";

type EditIdeaFormData = {
  title: string;
  lookingFor: string[] | string;
  expertise: string[] | string;
  description: string;
};

export const EditIdeaPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedIdea, loading, error } = useUnit($ideasStore);
  const [expertiseTerms, lookingForTerms] = useUnit([
    $expertiseTerms,
    $lookingForTerms,
  ]);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showArchiveConfirmModal, setShowArchiveConfirmModal] = useState(false);
  const { showError, showSuccess } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<EditIdeaFormData>({
    defaultValues: {
      title: "",
      lookingFor: [],
      expertise: [],
      description: "",
    },
  });

  useEffect(() => {
    if (id) {
      fetchIdeaDetailRequested(id);
    }
  }, [id]);

  // Initialize form with idea data
  useEffect(() => {
    if (selectedIdea) {
      setValue("title", selectedIdea.title);
      setValue("description", selectedIdea.description || "");

      // Handle looking_for field
      if (selectedIdea.looking_for) {
        const lookingForIds = selectedIdea.looking_for.map((item) =>
          typeof item === "string" ? item : item.id,
        );
        setValue("lookingFor", lookingForIds);
      }

      // Handle eu_expertise field
      if (selectedIdea.eu_expertise) {
        const expertiseIds = selectedIdea.eu_expertise.map((item) =>
          typeof item === "string" ? item : item.id,
        );
        setValue("expertise", expertiseIds);
      }
    }
  }, [selectedIdea, setValue]);

  const onSubmit = async (data: EditIdeaFormData) => {
    if (!selectedIdea?.id) {
      console.error("No idea ID available");
      return;
    }

    try {
      setEditLoading(true);

      // Ensure arrays are properly formatted
      const lookingFor = Array.isArray(data.lookingFor)
        ? data.lookingFor
        : data.lookingFor
          ? [data.lookingFor]
          : [];
      const expertise = Array.isArray(data.expertise)
        ? data.expertise
        : data.expertise
          ? [data.expertise]
          : [];

      const payload = {
        title: data.title,
        field_description: data.description,
        field_looking_for: lookingFor,
        field_eu_expertise: expertise,
      };

      // console.log("Updating idea:", payload);
      await ideasService.updateIdea(selectedIdea.id, payload);

      // console.log("Idea updated successfully");
      // Navigate back to idea detail page
      navigate(`/ideas/${selectedIdea.id}`);
    } catch (error: any) {
      console.error("Failed to update idea:", error);
      showError(error?.message || "Failed to update idea");
    } finally {
      setEditLoading(false);
    }
  };

  // Show confirmation modal for deleting idea
  const handleDeleteIdea = () => {
    setShowDeleteConfirmModal(true);
  };

  // Actual delete idea API call
  const confirmDeleteIdea = async () => {
    if (!selectedIdea?.id) {
      console.error("Idea not available");
      return;
    }

    try {
      setDeleteLoading(true);
      const patchConfig: AxiosRequestConfig = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      // For draft ideas, we typically use archive action to "delete" them
      await apiService.delete(
        API_ENDPOINTS.IDEAS.IDEA_DELETE(selectedIdea.id),
        patchConfig,
      );
      // console.log("Idea deleted successfully");
      showSuccess("Idea deleted successfully");
      // Navigate to ideas list
      navigate("/ideas");
    } catch (error: any) {
      console.error("Failed to delete idea:", error);
      showError(error?.error || "Failed to delete idea");
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirmModal(false);
    }
  };

  // Show confirmation modal for archiving idea
  const handleArchiveIdea = () => {
    setShowArchiveConfirmModal(true);
  };

  // Actual archive idea API call
  const confirmArchiveIdea = async () => {
    if (!selectedIdea?.id) {
      console.error("Idea not available");
      return;
    }

    try {
      setArchiveLoading(true);
      const patchConfig: AxiosRequestConfig = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      await apiService.patch(
        API_ENDPOINTS.IDEAS.IDEA_STATUS_ACTION(selectedIdea.id, "archive"),
        patchConfig,
      );
      // console.log("Idea archived successfully");
      showSuccess("Idea archived successfully");
      // Navigate to ideas list
      navigate("/ideas/my-ideas");
    } catch (error: any) {
      console.error("Failed to archive idea:", error);
      showError(error?.error || "Failed to archive idea");
    } finally {
      setArchiveLoading(false);
      setShowArchiveConfirmModal(false);
    }
  };

  const handleCancel = () => {
    if (selectedIdea?.id) {
      navigate(`/ideas/${selectedIdea.id}`);
    } else {
      navigate("/ideas");
    }
  };

  if (loading && !selectedIdea) {
    return (
      <div className="edit-idea-page">
        <Loader overlay />
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-idea-page">
        <div className="error-container">
          <h2>Error loading idea</h2>
          <p>{error}</p>
          <button onClick={() => navigate("/ideas")} className="btn-primary">
            Back to Ideas
          </button>
        </div>
      </div>
    );
  }

  if (!selectedIdea) {
    return (
      <div className="edit-idea-page">
        {/* <div className="error-container">
          <h2>Idea not found</h2>
          <button onClick={() => navigate('/ideas')} className="btn-primary">
            Back to Ideas
          </button>
        </div> */}
      </div>
    );
  }

  return (
    <div className="edit-idea-page">
      <div className="edit-idea-container">
        <div className="edit-idea-content">
          <div className="edit-idea-title">Edit your idea</div>
          <div className="edit-idea-description">
            {/* <pre>{JSON.stringify(selectedIdea, null, 2)}</pre> */}
            {selectedIdea?.state.toLowerCase() === "approved" &&
              "Your idea is published. To apply changes, republish after editing."}
            {selectedIdea.state.toLowerCase() === "draft" &&
              "Currently in draft: Work in progress"}
          </div>

          <form className="edit-idea-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="form-actions">
              <div
                className="form-actions-container"
                style={{ justifyContent: "flex-end" }}
              >
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={editLoading}
                >
                  {editLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                placeholder="What's your idea title?"
                {...register("title", { required: "Title is required" })}
              />
              {errors.title && (
                <span className="error">{errors.title.message}</span>
              )}
            </div>

            <div className="form-field">
              <label>Looking for</label>
              <div className="checkbox-group">
                {lookingForTerms.data?.map((term) => (
                  <label key={term.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      value={term.tid}
                      {...register("lookingFor", {
                        required: "Looking for Field is required",
                      })}
                    />
                    {term.name}
                  </label>
                ))}
              </div>
              {errors.lookingFor && (
                <span className="error">{errors.lookingFor.message}</span>
              )}
            </div>

            <div className="form-field">
              <label>With EU expertise in</label>
              <div className="checkbox-group">
                {expertiseTerms.data?.map((term) => (
                  <label key={term.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      value={term.tid}
                      {...register("expertise", {
                        required: "Expertise Field is required",
                      })}
                    />
                    {term.name}
                  </label>
                ))}
              </div>
              {errors.expertise && (
                <span className="error">{errors.expertise.message}</span>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                rows={4}
                placeholder="e.g. This project proposes a digital solution to enhance citizen engagement in local policymaking. The concept focuses on developing a multilingual platform co-created with municipalities and civic tech partners."
                {...register("description", {
                  required: "Description is required",
                })}
              ></textarea>
              {errors.description && (
                <span className="error">{errors.description.message}</span>
              )}
            </div>

            <div className="form-actions">
              <div className="form-actions-container">
                {(selectedIdea?.state.toLowerCase() == "draft" ||
                  selectedIdea?.state.toLowerCase() == "archived" ||
                  selectedIdea?.state.toLowerCase() == "declined") && (
                  <button
                    type="button"
                    className="btn-left delete-btn"
                    onClick={handleDeleteIdea}
                    disabled={deleteLoading}
                  >
                    <SvgIcon icon="icon-delete" />
                    {deleteLoading ? "Deleting..." : "Delete Idea"}
                  </button>
                )}
                {selectedIdea?.state.toLowerCase() == "approved" && (
                  <button
                    type="button"
                    className="btn-left archive-btn"
                    onClick={handleArchiveIdea}
                    disabled={archiveLoading}
                  >
                    <SvgIcon icon="icon-archive" />
                    {archiveLoading ? "Archiving..." : "Archive Idea"}
                  </button>
                )}
              </div>
              <div
                className="form-actions-container"
                style={{ justifyContent: "flex-end" }}
              >
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={editLoading}
                >
                  {editLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Delete Idea Confirmation Modal */}
      <CommonModal
        isOpen={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        hasForm={false}
        width={430}
        height={340}
        title="Delete Idea"
        icon="icon-delete"
      >
        <div className="confirmation-modal">
          <p>
            Are you sure you want to delete this idea? This action cannot be
            undone.
          </p>
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowDeleteConfirmModal(false)}
              disabled={deleteLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={confirmDeleteIdea}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete Idea"}
            </button>
          </div>
        </div>
      </CommonModal>

      {/* Archive Idea Confirmation Modal */}
      <CommonModal
        isOpen={showArchiveConfirmModal}
        onClose={() => setShowArchiveConfirmModal(false)}
        hasForm={false}
        width={430}
        height={340}
        title="Archive Idea"
        icon="icon-archive"
      >
        <div className="confirmation-modal">
          <p>
            Are you sure you want to archive this idea? You can restore it later
            if needed.
          </p>
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowArchiveConfirmModal(false)}
              disabled={archiveLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={confirmArchiveIdea}
              disabled={archiveLoading}
            >
              {archiveLoading ? "Archiving..." : "Archive Idea"}
            </button>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};
